import React, { useState } from "react";
import { Text } from "@ilc-technology/luik";
import Surface from "../../Surface/Surface";
import { useDatasources } from "../../../contexts/StoryblokContext/StoryblokContext";
import { formatDateRange, addDays, formatDate } from "../../../Common/Helpers/DateHelper";
import { FormatCurrency } from "../../../Common/Helpers/MoneyHelper";
import { Money, PaymentInfo, PaymentInfoStatus } from "../../../Common/Types";
import Enrich from "../../../Common/services/TextEnricher";
import { LabelKey } from "../../../Common/StoryblokTypes";
import { useSessionContext } from "../../../contexts/SessionContext/SessionContext";
import {
  formatQuoteTotalAsShownInQuoteHeader,
  getPaymentOrReservation,
  isQuoteFullyPaid,
} from "../../../Common/services/PaymentsService";
import TBCardStatus, { TBCardStatusAlignment, TBCardStatusPosition } from "../../BaseComponents/TBCardStatus";
import { ColorVariant } from "../../../Common/services/ColorService";
import TitleValueCell from "../../BaseComponents/TitleValueCell";
import PreferredQuoteToggle from "../../QuoteList/QuoteCard/PreferredQuoteToggle";
import StarIcon from "../../../assets/icons/Star.svg";
import StarOutlineIcon from "../../../assets/icons/StarOutline.svg";

interface QuoteHeaderProps {
  destinationName: string;
  courseInfo?: string;
  startAt: string;
  endAt: string;
  duration: string;
  totalPrice: Money;
  payments?: Array<PaymentInfo>;
  lgMinimized?: boolean;
  isPreferred?: boolean;
  onSetPreferred?: () => void;
}

const QuoteHeader: React.FC<QuoteHeaderProps> = ({
  destinationName,
  courseInfo,
  startAt,
  endAt,
  duration,
  totalPrice,
  payments,
  lgMinimized = true,
  isPreferred = false,
  onSetPreferred,
}) => {
  const { language } = useSessionContext();
  const { labels } = useDatasources();
  const [isOutlineStarHovered, setIsOutlineStarHovered] = useState(false);
  const preferredTooltipText = labels[LabelKey.preferredTooltip];

  const isQuotePaidOrReserved =
    payments && payments.find((x) => x.status === PaymentInfoStatus.Reserve || x.status === PaymentInfoStatus.Paid);
  const primaryPayment = getPaymentOrReservation(payments);

  const paymentMadeAt = primaryPayment?.madeAt
    ? primaryPayment?.status === PaymentInfoStatus.Paid
      ? formatDate(primaryPayment?.madeAt, language)
      : formatDate(addDays(new Date(primaryPayment?.madeAt), 1).toString(), language)
    : undefined;
  const enricherContext = isQuotePaidOrReserved
    ? {
        paymentStatus: primaryPayment?.status,
        paymentMadeAt: paymentMadeAt,
      }
    : {
        paymentStatus: "",
        paymentMadeAt: undefined,
      };
  const paymentInfoLabel = "paymentInfo_";
  const borderRadius = isQuotePaidOrReserved ? "bottom" : "all";
  const priceColumn = "border-t border-neutral-300 lg:border-t-0 lg:border-l lg:w-[275px] lg:items-end";
  const displayedHeaderAmount = formatQuoteTotalAsShownInQuoteHeader(totalPrice, payments);

  const isQuoteFullyPaidStatus = isQuoteFullyPaid(payments);
  const isPreferredQuoteVisible = !isQuoteFullyPaidStatus && (isPreferred || Boolean(onSetPreferred));

  return (
    <>
      <div
        className={`relative flex flex-col ${lgMinimized && "lg:mt-side-bar-top lg:sticky lg:top-6 lg:max-w-side-bar lg:self-start"}`}
      >
        {isQuotePaidOrReserved && (
          <TBCardStatus
            variant={
              isQuotePaidOrReserved && primaryPayment?.status == PaymentInfoStatus.Reserve
                ? ColorVariant.Yellow
                : ColorVariant.Green
            }
            alignment={TBCardStatusAlignment.Center}
            position={TBCardStatusPosition.OverCard}
            testId="payment-component"
          >
            <Text variant="label-sm-bold">
              {Enrich(labels[paymentInfoLabel + primaryPayment?.status], enricherContext)}
            </Text>
          </TBCardStatus>
        )}
        <Surface borderRadius={borderRadius}>
          <div className={"a-gap flex flex-col lg:max-h-[233px] lg:flex-row lg:justify-between"}>
            <div className="a-gap flex flex-col">
              <div className="flex items-start justify-between gap-2">
                <Text variant="special-eyebrow">
                  {formatDateRange(startAt, endAt, language)} | {duration}
                </Text>
                {isPreferredQuoteVisible && (
                  <div className="inline-flex shrink-0 items-center lg:hidden">
                    {isPreferred ? (
                      <img src={StarIcon} alt="" aria-hidden="true" className="size-4" />
                    ) : (
                      <button
                        type="button"
                        aria-label={labels[LabelKey.setAsPreferred]}
                        className="inline-flex size-4 cursor-pointer items-center justify-center"
                        onClick={(e) => {
                          e.stopPropagation();
                          onSetPreferred?.();
                        }}
                      >
                        <img src={StarOutlineIcon} alt="" aria-hidden="true" className="pointer-events-none size-4" />
                      </button>
                    )}
                  </div>
                )}
              </div>
              <div>
                <Text variant="heading-3-bold">{destinationName}</Text>
                {courseInfo && <Text variant="paragraph-small-body">{courseInfo}</Text>}
              </div>
            </div>
            <div className={`gap-small flex flex-col justify-start pt-6 lg:gap-2 lg:pt-0 ${priceColumn}`}>
              {isPreferredQuoteVisible && (
                <div
                  data-testid="quote-header-status-preferred"
                  className="hidden items-center gap-2 self-end lg:inline-flex"
                >
                  <PreferredQuoteToggle
                    isPreferred={isPreferred}
                    isOutlineStarHovered={isOutlineStarHovered}
                    preferredTooltipText={preferredTooltipText}
                    preferredQuoteLabel={labels[LabelKey.preferredQuote]}
                    setAsPreferredLabel={labels[LabelKey.setAsPreferred]}
                    onHoverChange={setIsOutlineStarHovered}
                    withTrailingPadding={false}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSetPreferred?.();
                    }}
                  />
                </div>
              )}
              <TitleValueCell
                leftTextValue={labels[LabelKey.total]}
                rightTextValue={displayedHeaderAmount}
                leftTextVariant="heading-5"
                rightTextVariant="heading-5-bold"
                className="flex flex-row items-center justify-between gap-2 lg:flex-col lg:items-end"
                testId="quote-header-total"
              />

              {isQuotePaidOrReserved && primaryPayment?.status === PaymentInfoStatus.Paid && (
                <div
                  className="flex flex-row items-center justify-between gap-4 lg:flex-col lg:items-end"
                  data-testid="paid-details-row"
                >
                  <Text className="label-lg lg:label-xl text-gray-45" variant="">
                    {labels[LabelKey.paymentsMade]}
                  </Text>
                  <Text
                    className="label-lg-bold lg:label-xl-bold text-gray-45"
                    // @ts-expect-error We need to pass something to variant otherwise it won't work on mobile
                    variant=""
                  >
                    {FormatCurrency(primaryPayment.payment.amount, totalPrice.currency)}
                  </Text>
                </div>
              )}
            </div>
          </div>
        </Surface>
      </div>
    </>
  );
};

export default QuoteHeader;
